// pages/ProjectTaskDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  MoreVertical,
  FileText,
  Activity,
  Save,
  X as CloseIcon,
  Loader,
  CheckSquare,
  Calendar,
  User,
  Clock,
  AlertCircle,
  CheckCircle,
  Circle,
  Pause,
  XCircle,
  Ban,
  MessageSquare,
  Paperclip,
  Building2,
  Briefcase,
  Flag,
  Target,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";
import apiService from "../../services/api";
import EditProjectTaskModal from "../../components/EditProjectTaskModal";
import { useTheme } from "../../contexts/ThemeContext";

const ProjectTaskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // Comments
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);

  // Notes
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [isPrivateNote, setIsPrivateNote] = useState(false);
  const [submittingNote, setSubmittingNote] = useState(false);

  // Attachments
  const [attachments, setAttachments] = useState([]);

  // Inline editing
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [savingField, setSavingField] = useState(false);

  useEffect(() => {
    fetchTask();
  }, [id]);

  const fetchTask = async () => {
    try {
      setLoading(true);
      const response = await apiService.getProjectTask(id);
      setTask(response.data);
      setComments(response.data.comments || []);
      setNotes(response.data.notes || []);
      setAttachments(response.data.attachments || []);
    } catch (error) {
      console.error("Error fetching task:", error);
      navigate("/tasks");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this task?")) {
      try {
        await apiService.deleteProjectTask(id);
        navigate("/tasks");
      } catch (error) {
        console.error("Error deleting task:", error);
        alert("Failed to delete task");
      }
    }
  };

  const startEditing = (field, currentValue) => {
    setEditingField(field);
    setEditingValue(currentValue || "");
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditingValue("");
  };

  const saveFieldEdit = async (field) => {
    try {
      setSavingField(true);
      await apiService.updateProjectTask(id, { [field]: editingValue });
      await fetchTask();
      setEditingField(null);
      setEditingValue("");
    } catch (error) {
      console.error("Error saving field:", error);
      alert("Failed to save changes");
    } finally {
      setSavingField(false);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      setSubmittingComment(true);
      await apiService.createProjectTaskComment(id, { content: newComment });
      setNewComment("");
      await fetchTask();
    } catch (error) {
      console.error("Error creating comment:", error);
      alert("Failed to create comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleNoteSubmit = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      setSubmittingNote(true);
      await apiService.createProjectTaskNote(id, {
        title: newNoteTitle,
        body: newNote,
        isPrivate: isPrivateNote,
      });
      setNewNote("");
      setNewNoteTitle("");
      setIsPrivateNote(false);
      await fetchTask();
    } catch (error) {
      console.error("Error creating note:", error);
      alert("Failed to create note");
    } finally {
      setSubmittingNote(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      "Not Started": "text-gray-400 bg-gray-400/10 border-gray-400/20",
      "In Progress": "text-blue-400 bg-blue-400/10 border-blue-400/20",
      "On Hold": "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
      Deferred: "text-orange-400 bg-orange-400/10 border-orange-400/20",
      Cancelled: "text-red-400 bg-red-400/10 border-red-400/20",
      Completed: "text-green-400 bg-green-400/10 border-green-400/20",
    };
    return colors[status] || "text-gray-400 bg-gray-400/10 border-gray-400/20";
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Not Started":
        return <Circle size={14} />;
      case "In Progress":
        return <TrendingUp size={14} />;
      case "On Hold":
        return <Pause size={14} />;
      case "Deferred":
        return <Clock size={14} />;
      case "Cancelled":
        return <Ban size={14} />;
      case "Completed":
        return <CheckCircle size={14} />;
      default:
        return <Circle size={14} />;
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      Low: "text-gray-400 bg-gray-400/10 border-gray-400/20",
      Medium: "text-blue-400 bg-blue-400/10 border-blue-400/20",
      High: "text-orange-400 bg-orange-400/10 border-orange-400/20",
      Urgent: "text-red-400 bg-red-400/10 border-red-400/20",
    };
    return (
      colors[priority] || "text-gray-400 bg-gray-400/10 border-gray-400/20"
    );
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case "Low":
        return <Flag size={14} />;
      case "Medium":
        return <Flag size={14} />;
      case "High":
        return <Flag size={14} />;
      case "Urgent":
        return <AlertTriangle size={14} />;
      default:
        return <Flag size={14} />;
    }
  };

  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === "Completed" || status === "Cancelled")
      return false;
    return new Date(dueDate) < new Date();
  };

  const EditableField = ({
    label,
    field,
    value,
    type = "text",
    options = null,
    readOnly = false,
  }) => {
    const isEditing = editingField === field;

    if (readOnly) {
      return (
        <div>
          <label
            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
          >
            {label}
          </label>
          <p className={isDarkMode ? "text-white" : "text-gray-900"}>
            {value || "—"}
          </p>
        </div>
      );
    }

    return (
      <div className="group">
        <label
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
        >
          {label}
        </label>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <div className="flex-1 flex items-center gap-2">
              {options ? (
                <select
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 rounded-lg focus:outline-none ${isDarkMode ? "bg-gray-800 border border-blue-500 text-white" : "bg-white border border-blue-500 text-gray-900"}`}
                  autoFocus
                >
                  <option value="">Select...</option>
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : type === "textarea" ? (
                <textarea
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 rounded-lg focus:outline-none resize-none ${isDarkMode ? "bg-gray-800 border border-blue-500 text-white" : "bg-white border border-blue-500 text-gray-900"}`}
                  rows={3}
                  autoFocus
                />
              ) : type === "date" ? (
                <input
                  type="date"
                  value={editingValue ? editingValue.split("T")[0] : ""}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 rounded-lg focus:outline-none ${isDarkMode ? "bg-gray-800 border border-blue-500 text-white" : "bg-white border border-blue-500 text-gray-900"}`}
                  autoFocus
                />
              ) : type === "number" ? (
                <input
                  type="number"
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 rounded-lg focus:outline-none ${isDarkMode ? "bg-gray-800 border border-blue-500 text-white" : "bg-white border border-blue-500 text-gray-900"}`}
                  autoFocus
                />
              ) : (
                <input
                  type={type}
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 rounded-lg focus:outline-none ${isDarkMode ? "bg-gray-800 border border-blue-500 text-white" : "bg-white border border-blue-500 text-gray-900"}`}
                  autoFocus
                />
              )}
              <button
                onClick={() => saveFieldEdit(field)}
                disabled={savingField}
                className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 text-white"
              >
                {savingField ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
              </button>
              <button
                onClick={cancelEditing}
                className={`p-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                <CloseIcon size={16} />
              </button>
            </div>
          ) : (
            <>
              <p
                className={`flex-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {type === "date" && value
                  ? new Date(value).toLocaleDateString()
                  : value || "—"}
              </p>
              <button
                onClick={() => startEditing(field, value)}
                className={`opacity-0 group-hover:opacity-100 p-1 rounded transition-all ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
              >
                <Edit
                  size={14}
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"}`}
      >
        <Loader
          className={
            isDarkMode
              ? "text-white animate-spin"
              : "text-gray-900 animate-spin"
          }
          size={32}
        />
      </div>
    );
  }

  if (!task) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"}`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Task not found
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      {/* Header */}
      <div
        className={`border-b transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
      >
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
            >
              <ArrowLeft size={20} />
              <span>Go Back</span>
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-white"
              >
                <Edit size={16} />
                <span>Edit</span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className={`p-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-100 hover:bg-gray-200"}`}
                >
                  <MoreVertical
                    size={20}
                    className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                  />
                </button>

                {showActionsMenu && (
                  <div
                    className={`absolute right-0 top-12 w-48 rounded-lg shadow-xl z-50 border ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                  >
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        setShowActionsMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left flex items-center gap-2 ${isDarkMode ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-50 text-gray-700"}`}
                    >
                      <FileText size={16} />
                      Copy Link
                    </button>
                    <div
                      className={
                        isDarkMode
                          ? "border-t border-gray-700"
                          : "border-t border-gray-200"
                      }
                    ></div>
                    <button
                      onClick={() => {
                        handleDelete();
                        setShowActionsMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left flex items-center gap-2 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Task Header */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white">
                  <CheckSquare size={24} />
                </div>
                <div>
                  <h1
                    className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {task.projectTaskName}
                  </h1>
                  {task.subject && (
                    <div
                      className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                    >
                      {task.subject}
                    </div>
                  )}
                </div>

                {task.status && (
                  <span
                    className={`px-3 py-1 rounded-lg border text-sm font-medium flex items-center gap-1.5 ${getStatusColor(task.status)}`}
                  >
                    {getStatusIcon(task.status)}
                    {task.status}
                  </span>
                )}

                {task.priority && (
                  <span
                    className={`px-3 py-1 rounded-lg border text-sm font-medium flex items-center gap-1.5 ${getPriorityColor(task.priority)}`}
                  >
                    {getPriorityIcon(task.priority)}
                    {task.priority}
                  </span>
                )}

                {task.isBlocked && (
                  <span className="px-3 py-1 rounded-lg border text-sm font-medium bg-red-500/10 text-red-400 border-red-500/20 flex items-center gap-1.5">
                    <AlertCircle size={14} />
                    Blocked
                  </span>
                )}
              </div>

              <div
                className={`flex flex-wrap items-center gap-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {task.project && (
                  <button
                    onClick={() => navigate(`/projects/${task.project.id}`)}
                    className={`flex items-center gap-2 transition-colors ${isDarkMode ? "hover:text-blue-400" : "hover:text-blue-600"}`}
                  >
                    <Briefcase size={16} />
                    <span>{task.project.projectName}</span>
                  </button>
                )}
                {task.account && (
                  <button
                    onClick={() => navigate(`/accounts/${task.account.id}`)}
                    className={`flex items-center gap-2 transition-colors ${isDarkMode ? "hover:text-blue-400" : "hover:text-blue-600"}`}
                  >
                    <Building2 size={16} />
                    <span>{task.account.name}</span>
                  </button>
                )}
                {task.dueDate && (
                  <div
                    className={`flex items-center gap-2 ${isOverdue(task.dueDate, task.status) ? "text-red-400" : ""}`}
                  >
                    <Calendar size={16} />
                    <span>
                      Due {new Date(task.dueDate).toLocaleDateString()}
                    </span>
                    {isOverdue(task.dueDate, task.status) && (
                      <AlertTriangle size={14} />
                    )}
                  </div>
                )}
              </div>
            </div>

            {task.assignedUser && (
              <div
                className={`flex items-center gap-3 rounded-lg p-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? "bg-gray-700" : "bg-gray-300"}`}
                >
                  {task.assignedUser.profileImageUrl ? (
                    <img
                      src={task.assignedUser.profileImageUrl}
                      alt={task.assignedUser.name}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <span
                      className={`text-lg font-medium ${isDarkMode ? "text-white" : "text-gray-700"}`}
                    >
                      {task.assignedUser.name?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <div
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Assigned To
                  </div>
                  <div
                    className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {task.assignedUser.name}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          {task.percentComplete !== undefined && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Progress
                </span>
                <span
                  className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {task.percentComplete}%
                </span>
              </div>
              <div
                className={`w-full h-2 rounded-full overflow-hidden ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
              >
                <div
                  className="h-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${task.percentComplete}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div
              className={`rounded-lg border transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div
                className={`flex border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                {[
                  { id: "details", label: "Details", icon: FileText },
                  { id: "comments", label: "Comments", icon: MessageSquare },
                  { id: "activity", label: "Activity", icon: Activity },
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-6 py-3 font-medium transition-colors ${
                        activeTab === tab.id
                          ? isDarkMode
                            ? "text-blue-400 border-b-2 border-blue-400"
                            : "text-blue-600 border-b-2 border-blue-600"
                          : isDarkMode
                            ? "text-gray-400 hover:text-white"
                            : "text-gray-500 hover:text-gray-900"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon size={16} />
                        {tab.label}
                        {tab.id === "comments" && comments.length > 0 && (
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                            {comments.length}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="p-6">
                {/* Details Tab */}
                {activeTab === "details" && (
                  <div className="space-y-6">
                    {/* Description */}
                    {task.description && (
                      <div>
                        <h3
                          className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          Description
                        </h3>
                        <div
                          className={`rounded-lg p-4 ${isDarkMode ? "text-gray-300 bg-gray-800/50" : "text-gray-600 bg-gray-50"}`}
                        >
                          {task.description}
                        </div>
                      </div>
                    )}

                    {/* Basic Information */}
                    <div
                      className={`pt-6 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <CheckSquare size={18} />
                        Task Information
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <EditableField
                          label="Task Name"
                          field="projectTaskName"
                          value={task.projectTaskName}
                        />
                        <EditableField
                          label="Subject"
                          field="subject"
                          value={task.subject}
                        />
                        <EditableField
                          label="Status"
                          field="status"
                          value={task.status}
                          options={[
                            { value: "Not Started", label: "Not Started" },
                            { value: "In Progress", label: "In Progress" },
                            { value: "On Hold", label: "On Hold" },
                            { value: "Deferred", label: "Deferred" },
                            { value: "Cancelled", label: "Cancelled" },
                            { value: "Completed", label: "Completed" },
                          ]}
                        />
                        <EditableField
                          label="Priority"
                          field="priority"
                          value={task.priority}
                          options={[
                            { value: "Low", label: "Low" },
                            { value: "Medium", label: "Medium" },
                            { value: "High", label: "High" },
                            { value: "Urgent", label: "Urgent" },
                          ]}
                        />
                        <EditableField
                          label="Progress"
                          field="percentComplete"
                          value={`${task.percentComplete}%`}
                          type="number"
                        />
                      </div>
                    </div>

                    {/* Dates */}
                    <div
                      className={`pt-6 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Calendar size={18} />
                        Timeline
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <EditableField
                          label="Start Date"
                          field="startDate"
                          value={task.startDate}
                          type="date"
                        />
                        <EditableField
                          label="Due Date"
                          field="dueDate"
                          value={task.dueDate}
                          type="date"
                        />
                        {task.completedDate && (
                          <div className="col-span-2">
                            <EditableField
                              label="Completed Date"
                              field="completedDate"
                              value={task.completedDate}
                              type="date"
                              readOnly
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Time Tracking */}
                    <div
                      className={`pt-6 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Clock size={18} />
                        Time Tracking
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <EditableField
                          label="Estimated Hours"
                          field="estimatedHours"
                          value={task.estimatedHours}
                          type="number"
                        />
                        <EditableField
                          label="Actual Hours"
                          field="actualHours"
                          value={task.actualHours}
                          type="number"
                        />
                      </div>
                    </div>

                    {/* Blocking Information */}
                    {task.isBlocked && (
                      <div
                        className={`pt-6 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
                      >
                        <h3
                          className={`text-lg font-semibold mb-4 flex items-center gap-2 text-red-400`}
                        >
                          <AlertCircle size={18} />
                          Blocked
                        </h3>
                        <div
                          className={`rounded-lg p-4 ${isDarkMode ? "bg-red-500/10 border border-red-500/20" : "bg-red-50 border border-red-200"}`}
                        >
                          <EditableField
                            label="Reason"
                            field="blockedReason"
                            value={task.blockedReason}
                            type="textarea"
                          />
                        </div>
                      </div>
                    )}

                    {/* Labels */}
                    {task.labels && task.labels.length > 0 && (
                      <div
                        className={`pt-6 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
                      >
                        <h3
                          className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          Labels
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {task.labels.map((label, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-full text-sm"
                            >
                              {label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* System Information */}
                    <div
                      className={`pt-6 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        System Information
                      </h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Created By
                          </label>
                          <p
                            className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <User size={16} />
                            {task.creator?.name || "System"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Created Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(task.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Last Modified By
                          </label>
                          <p
                            className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <User size={16} />
                            {task.lastModifiedBy?.name || "—"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Last Modified Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(task.updatedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Comments Tab */}
                {activeTab === "comments" && (
                  <div className="space-y-6">
                    {/* Comment Form */}
                    <form onSubmit={handleCommentSubmit} className="space-y-4">
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment..."
                        rows={4}
                        className={`w-full px-4 py-3 rounded-lg resize-none focus:outline-none transition-colors ${isDarkMode ? "bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:border-blue-500" : "bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-400"}`}
                      />
                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={submittingComment || !newComment.trim()}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-white"
                        >
                          {submittingComment ? (
                            <>
                              <Loader size={16} className="animate-spin" />
                              Posting...
                            </>
                          ) : (
                            "Post Comment"
                          )}
                        </button>
                      </div>
                    </form>

                    {/* Comments List */}
                    <div className="space-y-4">
                      {comments.length === 0 ? (
                        <div
                          className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          <MessageSquare
                            size={48}
                            className="mx-auto mb-2 opacity-50"
                          />
                          <p>No comments yet</p>
                        </div>
                      ) : (
                        comments.map((comment) => (
                          <div
                            key={comment.id}
                            className={`flex gap-3 rounded-lg p-4 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"}`}
                          >
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
                            >
                              {comment.creator?.profileImageUrl ? (
                                <img
                                  src={comment.creator.profileImageUrl}
                                  alt={comment.creator.name}
                                  className="w-full h-full rounded-full object-cover"
                                />
                              ) : (
                                <span
                                  className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-600"}`}
                                >
                                  {comment.creator?.name
                                    ?.charAt(0)
                                    .toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <span
                                  className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                >
                                  {comment.creator?.name}
                                </span>
                                <span
                                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                >
                                  {new Date(comment.createdAt).toLocaleString()}
                                </span>
                              </div>
                              <p
                                className={
                                  isDarkMode ? "text-gray-300" : "text-gray-600"
                                }
                              >
                                {comment.content}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Activity Tab */}
                {activeTab === "activity" && (
                  <div
                    className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <Activity size={48} className="mx-auto mb-2 opacity-50" />
                    <p>Activity tracking coming soon</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Quick Info Card */}
            <div
              className={`rounded-lg border p-4 transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <h3
                className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Quick Information
              </h3>
              <div className="space-y-3">
                {task.project && (
                  <button
                    onClick={() => navigate(`/projects/${task.project.id}`)}
                    className={`w-full p-3 rounded-lg transition-colors text-left ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <Briefcase size={14} />
                      Project
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {task.project.projectName}
                    </div>
                    {task.project.projectNumber && (
                      <div
                        className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        #{task.project.projectNumber}
                      </div>
                    )}
                  </button>
                )}

                {task.account && (
                  <button
                    onClick={() => navigate(`/accounts/${task.account.id}`)}
                    className={`w-full p-3 rounded-lg transition-colors text-left ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <Building2 size={14} />
                      Account
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {task.account.name}
                    </div>
                  </button>
                )}

                {task.taskTemplate && (
                  <div
                    className={`p-3 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"}`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <Target size={14} />
                      Template
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {task.taskTemplate.taskTemplateName}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Progress Card */}
            <div
              className={`rounded-lg border p-4 transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <h3
                className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Progress
              </h3>
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-4xl font-bold text-blue-400 mb-2">
                    {task.percentComplete}%
                  </div>
                  <div
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Complete
                  </div>
                </div>

                {task.estimatedHours && (
                  <div
                    className={`flex items-center justify-between pt-4 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
                  >
                    <span
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Estimated
                    </span>
                    <span
                      className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {task.estimatedHours}h
                    </span>
                  </div>
                )}

                {task.actualHours && (
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Actual
                    </span>
                    <span
                      className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {task.actualHours}h
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Timeline Card */}
            <div
              className={`rounded-lg border p-4 transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <h3
                className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Timeline
              </h3>
              <div className="space-y-3">
                {task.startDate && (
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Start Date
                    </span>
                    <span
                      className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {new Date(task.startDate).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {task.dueDate && (
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Due Date
                    </span>
                    <span
                      className={`text-sm ${isOverdue(task.dueDate, task.status) ? "text-red-400" : isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {new Date(task.dueDate).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {task.completedDate && (
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Completed
                    </span>
                    <span className="text-sm text-green-400">
                      {new Date(task.completedDate).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Attachments Card */}
            <div
              className={`rounded-lg border p-4 transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <h3
                className={`font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                <Paperclip size={16} />
                Attachments
                {attachments.length > 0 && (
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                    {attachments.length}
                  </span>
                )}
              </h3>
              {attachments.length === 0 ? (
                <p
                  className={`text-sm text-center py-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  No attachments
                </p>
              ) : (
                <div className="space-y-2">
                  {attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className={`p-3 rounded-lg transition-colors ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
                    >
                      <div className="flex items-center gap-2">
                        <Paperclip
                          size={14}
                          className={
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }
                        />
                        <span
                          className={`text-sm truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {attachment.fileName}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes Card */}
            <div
              className={`rounded-lg border p-4 transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <h3
                className={`font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                <FileText size={16} />
                Notes
                {notes.length > 0 && (
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                    {notes.length}
                  </span>
                )}
              </h3>
              {notes.length === 0 ? (
                <p
                  className={`text-sm text-center py-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  No notes
                </p>
              ) : (
                <div className="space-y-2">
                  {notes.slice(0, 3).map((note) => (
                    <div
                      key={note.id}
                      className={`p-3 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"}`}
                    >
                      <div
                        className={`font-medium text-sm mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {note.title}
                      </div>
                      <div
                        className={`text-xs line-clamp-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        {note.body}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <EditProjectTaskModal
          task={task}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchTask();
          }}
        />
      )}
    </div>
  );
};

export default ProjectTaskDetail;
